import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

const serviceLinks = [
  { name: 'Kitchen Remodeling', path: '/services#kitchen' },
  { name: 'Bathroom Remodeling', path: '/services#bathroom' },
  { name: 'Interior Renovations', path: '/services#interior' },
  { name: 'Flooring', path: '/services#flooring' },
  { name: 'Tile Work', path: '/services#tile' },
  { name: 'Painting', path: '/services#painting' },
];

const quickLinks = [
  { name: 'Our Process', path: '/process' },
  { name: 'Projects', path: '/projects' },
  { name: 'About Us', path: '/about' },
  { name: 'Service Areas', path: '/service-areas' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'Contact', path: '/contact' },
];

const legalLinks = [
  { name: 'Privacy Policy', path: '/privacy' },
  { name: 'Terms of Service', path: '/terms' },
  { name: 'Cookie Policy', path: '/cookies' },
  { name: 'Accessibility', path: '/accessibility' },
];

const serviceAreas = [
  'St. Augustine',
  'Jacksonville',
  'Ponte Vedra',
  'Nocatee',
  'St. Johns',
  'Palm Coast',
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" data-testid="footer-logo" aria-label="Jax Remodeling Solutions - Home">
              <img
                src={LOGO_URL}
                alt="Jax Remodeling Solutions - Luxury Home Remodeling in Northeast Florida"
                className="h-20 w-auto object-contain mb-6"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Luxury remodeling with precision project management and white-glove communication. Serving Northeast Florida with 19+ years of experience.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="tel:9048636673"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-white hover:text-[#C6A87C] transition-colors"
                aria-label="Call us at (904) 863-6673"
              >
                <Phone className="w-4 h-4 text-[#C6A87C]" aria-hidden="true" />
                <span className="text-sm">(904) 863-6673</span>
              </a>
              <a
                href="mailto:jaxremodelingsolutions@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-3 text-white hover:text-[#C6A87C] transition-colors"
                aria-label="Email us at jaxremodelingsolutions@gmail.com"
              >
                <Mail className="w-4 h-4 text-[#C6A87C]" aria-hidden="true" />
                <span className="text-sm">jaxremodelingsolutions@gmail.com</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-4 h-4 text-[#C6A87C] mt-0.5" aria-hidden="true" />
                <span className="text-sm">St. Augustine, FL</span>
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-heading text-lg mb-6">Services</h4>
            <ul className="space-y-3" role="list">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#C6A87C] text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="font-heading text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3" role="list">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#C6A87C] text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/employee/login"
                  className="text-gray-400 hover:text-[#C6A87C] text-sm transition-colors"
                >
                  Employee Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas Column */}
          <div>
            <h4 className="font-heading text-lg mb-6">Service Areas</h4>
            <ul className="space-y-3" role="list">
              {serviceAreas.map((area) => (
                <li key={area}>
                  <span className="text-gray-400 text-sm">{area}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <p className="text-gray-500 text-xs leading-relaxed" data-testid="compliance-notice">
            Jax Remodeling Solutions LLC provides remodeling and specialty trade services as permitted. We do not offer services requiring a Florida General Contractor license at this time. Where needed, we can coordinate with properly licensed professionals.
          </p>
        </div>
      </div>

      {/* Legal Links */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-gray-500 hover:text-[#C6A87C] text-xs transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs text-center md:text-left">
              &copy; {currentYear} Jax Remodeling Solutions LLC. All rights reserved.
              <br className="md:hidden" />
              <span className="hidden md:inline"> | </span>
              Intellectual Property of RJ Island Realty, LLC.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/faq" className="text-gray-500 hover:text-[#C6A87C] text-xs transition-colors">
                FAQ
              </Link>
              <span className="text-gray-700" aria-hidden="true">|</span>
              <Link to="/contact" className="text-gray-500 hover:text-[#C6A87C] text-xs transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
